import { BigBashEvent, Customer, Game, GamePoint, Player, RoomName, RoundPoint, Team } from "@/types/types";
import { idTimestampManager } from "@/utils/IDTimestampManager";
import SmallHelpers from "@/utils/SmallHelpers";

class MockEntityObjectCreator {
	teamNames = ['Team Alpha', 'Team Beta'];
	playerNames = ['Alice', 'Bob', 'Charlie', 'David'];
	private playerIndex = 0;
	private teamIndex = 0;
	private smallHelpers = new SmallHelpers();

	public createCustomer(): Customer {
		const customer = {
			...idTimestampManager.setInitialValues('Customer'),
			full_name: 'Robert Greene',
			email: 'robert.greene@example.com',
			phone: '+49 30 12345678'
		};

		return customer;
	}

	public createEmptyCustomer(): Customer {
		const customer = {
			...idTimestampManager.setInitialValues('Customer'),
			full_name: '',
			email: '',
			phone: ''
		};

		return customer;
	}

	public createPlayer(): Player {
		const player = {
			...idTimestampManager.setInitialValues('Player'),
			name: this.playerNames[this.playerIndex % this.playerNames.length]
		};
		this.playerIndex++;
		return player;
	}

	public createEmptyPlayer(): Player {
		const player = {
			...idTimestampManager.setInitialValues('Player'),
			name: ''
		};
		return player;
	}

	public createPlayers(count: number): Player[] {
		const players: Player[] = [];
		for (let i = 0; i < count; i++) {
			players.push(this.createPlayer());
		}
		return players;
	}

	public createEmptyPlayers(count: number): Player[] {
		const players: Player[] = [];
		for (let i = 0; i < count; i++) {
			players.push(this.createEmptyPlayer());
		}
		return players;
	}

	public createTeam(teamName?: string): Team {
		const team = {
			...idTimestampManager.setInitialValues('Team'),
			name: teamName || this.teamNames[this.teamIndex % this.teamNames.length],
			players: this.createPlayers(2),
			team_number: this.teamIndex + 1,
			color: this.smallHelpers.generateTeamColor(this.teamIndex)
		};
		this.teamIndex++;
		return team;
	}

	public createTeams(count: number): Team[] {
		const teams: Team[] = [];
		for (let i = 0; i < count; i++) {
			teams.push(this.createTeam());
		}
		return teams;
	}

	private defaultTeamNames: string[] = [
		'Schnelle Schnecken',
		'Viele Vögel',
		'Kahle Karpfen',
		'Olle Opas',
		'Zahme Zecken',
		'Scharfe Schafe',
		'Kalte Käfer',
		'Müde Mäuse',
		'Taube Tauben',
		'Giftige Gummigurken',
		'Krosse Krabben',
		'Mollige Molche',
		'Geile Geier',
		'Spießige Spinnen',
		'Laichende Lachse',
		'Laichende Lärchze',
		'Milde Milben',
		'Kapernde Kapern',
		'Motzende Motten',
		'Bellende Bieber',
		'Fette Frösche',
		'Wilde Wölfe',
		'Blöde Bären',
		'Nervige Nashörner',
		'Freche Fische',
	];

	private getTeamName(teamNumber: number): string {
		return this.defaultTeamNames[(teamNumber - 1) % this.defaultTeamNames.length];
	}

	public createEmptyTeam(teamNumber: number): Team {
		const team = {
			...idTimestampManager.setInitialValues('Team'),
			name: this.getTeamName(teamNumber),
			players: [],
			team_number: teamNumber,
			color: this.smallHelpers.generateTeamColor(teamNumber - 1)
		};
		return team;
	}

	public createEmptyTeams(count: number): Team[] {
		const teams: Team[] = [];
		for (let i = 0; i < count; i++) {
			teams.push(this.createEmptyTeam(i + 1));
		}
		return teams;
	}

	public createEvent(numberOfTeams?: number): BigBashEvent {
		const event = {
			...idTimestampManager.setInitialValues('Event'),
			name: 'JGA von Robert',
			datetime: new Date(),
			customer: this.createCustomer(),
			teams: this.createTeams(numberOfTeams || 2),
			created_at: new Date(),
			updated_at: new Date(),
			duration: 60,
			games: [this.createGame()],
			game_order: {},
			game_points: [],
			notes: 'Bitte keine Konfetti-Kanonen verwenden, Robert hat eine Allergie.'
		};

		return event;
	}

	public createEmptyEvent(): BigBashEvent {
		const event = {
			...idTimestampManager.setInitialValues('Event'),
			name: '',
			datetime: new Date(),
			customer: this.createEmptyCustomer(),
			teams: this.createEmptyTeams(2),
			created_at: new Date(),
			updated_at: new Date(),
			duration: 0,
			games: [],
			game_order: {},
			game_points: [],
			notes: ''
		};
		return event;
	}

	public createGame(roomName?: RoomName): Game {
		const game: Game = {
			...idTimestampManager.setInitialValues("Game"),
			name: "Quiz Battle",
			point_calculation: "most_points",
			default_room: roomName || "brain",
			description: "A fast-paced quiz game where teams compete to answer questions.",
			rules: "Answer as many questions correctly as possible within the time limit.",
			rule_video_url: "https://example.com/rules-video"
		};

		return game;
	}

	public createEmptyGameForRoom(roomName: RoomName): Game {
		const game: Game = {
			...idTimestampManager.setInitialValues("Game"),
			name: "",
			point_calculation: "",
			default_room: roomName,
			description: "",
			rules: "",
			rule_video_url: ""
		};

		return game;
	}

	public createGamePoint(initialRoundNo: number, initialPoints: number, team?: Team, game?: Game): GamePoint {
		const gamePoint = {
			...idTimestampManager.setInitialValues("GamePoint"),
			game: game.id || this.createGame().id,
			team: team.id || this.createTeam().id,
			rounds: [this.createRoundPoint(initialRoundNo, initialPoints)]
		};

		return gamePoint;
	}

	public createRoundPoint(roundNo: number, points: number): RoundPoint {
		return {
			...idTimestampManager.setInitialValues("RoundPoint"),
			round: roundNo,
			points: points
		};
	}

	public createGamePointWithRounds(team: Team, game: Game, pointsPerRound: number[]): GamePoint {
		const rounds = pointsPerRound.map((points, index) =>
			this.createRoundPoint(index, points)
		);

		return {
			...idTimestampManager.setInitialValues("GamePoint"),
			game: game.id,
			team: team.id,
			rounds: rounds
		};
	}

	public createGamePointWithFastestTimes(team: Team, game: Game, timesPerRound: number[]): GamePoint {
		const rounds = timesPerRound.map((time, index) => ({
			...idTimestampManager.setInitialValues("RoundPoint"),
			round: index,
			points: null,
			team_fastest_time_seconds: time,
			team_smallest_difference_value: null,
			round_target_value: null,
		}));

		return {
			...idTimestampManager.setInitialValues("GamePoint"),
			game: game.id,
			team: team.id,
			rounds: rounds
		};
	}

	public createGamePointWithSmallestDifference(
		team: Team,
		game: Game,
		valuesPerRound: number[],
		targetValues: number[]
	): GamePoint {
		const rounds = valuesPerRound.map((value, index) => ({
			...idTimestampManager.setInitialValues("RoundPoint"),
			round: index,
			points: null,
			team_fastest_time_seconds: null,
			team_smallest_difference_value: value,
			round_target_value: targetValues[index],
		}));

		return {
			...idTimestampManager.setInitialValues("GamePoint"),
			game: game.id,
			team: team.id,
			rounds: rounds
		};
	}

	public createGamePointWithAllTypes(
		team: Team,
		game: Game,
		pointsPerRound: (number | null)[],
		timesPerRound: (number | null)[],
		diffValuesPerRound: (number | null)[],
		targetValues: number[]
	): GamePoint {
		const maxRounds = Math.max(
			pointsPerRound.length,
			timesPerRound.length,
			diffValuesPerRound.length
		);

		const rounds = [];
		for (let i = 0; i < maxRounds; i++) {
			rounds.push({
				...idTimestampManager.setInitialValues("RoundPoint"),
				round: i,
				points: pointsPerRound[i] ?? null,
				team_fastest_time_seconds: timesPerRound[i] ?? null,
				team_smallest_difference_value: diffValuesPerRound[i] ?? null,
				round_target_value: targetValues[i] ?? null,
			});
		}

		return {
			...idTimestampManager.setInitialValues("GamePoint"),
			game: game.id,
			team: team.id,
			rounds: rounds
		};
	}

}

export default MockEntityObjectCreator;	